import * as Icons from '@ant-design/icons';
import {
  Button,
  Card,
  Dropdown,
  MenuProps,
  Select,
  Space,
  Table,
  Tooltip,
  Typography
} from 'antd';
import { HttpError, useNotification } from '@refinedev/core';
import { IIssuedCertificate } from 'interfaces/course_certificate';
import {
  IIssuedCertificateFilters,
  IssuedCertificatesFilter
} from './IssuedCertificatesFilter';
import { ImportCertificateModal } from 'components/modal/certificates/ImportCertificateModal';
import { List, ShowButton, TextField, useTable } from '@refinedev/antd';
import { StudentInfoCard } from '../ReadyForCertificateTab/StudentInfoCard';
import { createFilters } from 'utils/create-filters';
import { downloadFileFromBlob } from 'utils/donwload-from-blob';
import { formatDate } from 'services/date';
import { generateURLWithFilters } from 'services/certificate';
import { getAPI } from 'requests/api';
import { useCallback, useMemo, useState } from 'react';
import { useCertificateReport } from 'hooks/useCertificateReport';

const { Text } = Typography;

const REPORT_STORAGE_KEY = 'merged_graduate_course_certificates_report_id';
const MERGED_PDF_KIND = 'merged_pdf';
const ACADEMIC_RECORDS_KIND = 'academic_records';

// Espelham os tetos da API (`MergedGraduateCourseCertificates::Generate` e
// `MergedAcademicRecords::Generate`). Se divergirem, a opcao fica habilitada aqui e
// o lote falha la, entao os quatro numeros mudam juntos ou nao mudam.
//
// Os dois valem 100 hoje, e sao duas constantes de proposito: sao tetos de lotes
// diferentes que por ora coincidem, nao o mesmo teto escrito duas vezes.
const MAX_CERTIFICATES = 100;
const MAX_ACADEMIC_RECORDS = 100;

const CSV_KIND = 'csv';

// O lote sai do filtro aplicado, entao baixar sem filtro seria exportar a base.
const DOWNLOAD_OPTIONS = [
  {
    key: MERGED_PDF_KIND,
    label: 'Certificados em PDF para impressão',
    max: MAX_CERTIFICATES
  },
  {
    key: ACADEMIC_RECORDS_KIND,
    label: 'Históricos em PDF para impressão',
    max: MAX_ACADEMIC_RECORDS
  },
  { key: CSV_KIND, label: 'Relatório em CSV', max: undefined }
];

// Quantos certificados a secretaria quer nesta leva. Sem isto, encurtar o lote
// significava apertar o filtro ate o total caber no teto — e um filtro com 800
// nao saia de jeito nenhum. Com limite, sai a primeira leva; combinado com o
// filtro "nao exportados", a leva seguinte pega os proximos.
const NO_BATCH_LIMIT = 0;
const BATCH_LIMIT_OPTIONS = [10, 20, 50, 100];

const BATCH_LIMIT_SELECT_OPTIONS = [
  { value: NO_BATCH_LIMIT, label: 'Lote: sem limite' },
  ...BATCH_LIMIT_OPTIONS.map(size => ({ value: size, label: `Lote: ${size}` }))
];

interface DownloadMenuProps {
  hasFilter: boolean;
  busy: boolean;
  total?: number;
  limit: number;
  reportId: string;
  runningKind: string;
  onSelect: (key: string) => void;
}

const blockedReason = (hasFilter: boolean, running: boolean) => {
  if (running) {
    return 'Aguarde o download em andamento terminar';
  }

  if (!hasFilter) {
    return 'Aplique um filtro para baixar em lote';
  }

  return '';
};

const DownloadMenu = ({
  hasFilter,
  busy,
  total,
  limit,
  reportId,
  runningKind,
  onSelect
}: DownloadMenuProps) => {
  const running = !!reportId || busy;
  const count = total ?? 0;
  const blocked = blockedReason(hasFilter, running);

  // Com limite, o que entra no PDF e o limite, nao o total do filtro — e por isso
  // que uma leva de 50 sai de um filtro com 800. Sem limite, vale o total, como antes.
  const batchSize = limit || count;

  // Cada opcao tem seu proprio teto, entao a que estourar fica desabilitada
  // sozinha em vez de derrubar o menu inteiro.
  const items: MenuProps['items'] = DOWNLOAD_OPTIONS.map(option => {
    const exceeded = option.max !== undefined && batchSize > option.max;

    return {
      key: option.key,
      disabled: exceeded || count === 0,
      label: exceeded ? `${option.label} — máx. ${option.max}` : option.label
    };
  });

  const runningLabel = DOWNLOAD_OPTIONS.find(
    option => option.key === runningKind
  )?.label;

  return (
    // O Dropdown repassa `disabled` ao Button, e botao desabilitado nao dispara
    // evento de mouse: sem o span por fora e o pointer-events por dentro, o
    // Tooltip que explica o bloqueio nunca aparece.
    <Tooltip title={blocked}>
      <span className={blocked ? 'cursor-not-allowed' : undefined}>
        <Dropdown
          menu={{ items, onClick: event => onSelect(event.key) }}
          disabled={!!blocked}
        >
          <Button
            icon={<Icons.DownloadOutlined />}
            loading={running}
            style={blocked ? { pointerEvents: 'none' } : undefined}
          >
            <Space>
              {running ? `Gerando ${runningLabel ?? 'arquivo'}...` : 'Baixar'}
              {!running && <Icons.DownOutlined />}
            </Space>
          </Button>
        </Dropdown>
      </span>
    </Tooltip>
  );
};

const IssuedCertificates = () => {
  const notification = useNotification();
  const { reportId, runningKind, generate } =
    useCertificateReport(REPORT_STORAGE_KEY);
  // O relatorio precisa sair com o mesmo filtro que produziu o total exibido.
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<IIssuedCertificateFilters>
  >({});
  // O CSV e sincrono e nao passa pelo hook, entao precisa do proprio loading
  // para nao aceitar clique repetido enquanto o arquivo nao volta.
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [batchLimit, setBatchLimit] = useState<number>(NO_BATCH_LIMIT);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importType, setImportType] = useState<
    'regulator_delivery' | 'secretary_receipt' | 'tracking_code'
  >('regulator_delivery');

  const { tableProps, searchFormProps } = useTable<
    IIssuedCertificate,
    HttpError,
    IIssuedCertificateFilters
  >({
    onSearch: params => {
      setAppliedFilters(params);

      return createFilters({
        by_registration_number: params.by_registration_number,
        by_user_name: params.by_user_name,
        by_cpf: params.by_cpf,
        by_user_email: params.by_user_email,
        by_course_code: params.by_course_code,
        issued_after: params.issued_after,
        issued_before: params.issued_before,
        registered_after: params.registered_after,
        registered_before: params.registered_before,
        by_export_status: params.by_export_status
      });
    }
  });

  const handleDownloadCertificate = useCallback(async () => {
    setDownloadingCsv(true);

    try {
      const response = await getAPI().get(
        generateURLWithFilters<IIssuedCertificateFilters>(
          appliedFilters,
          'admin/graduate/courses/certificates/report'
        ),
        {
          responseType: 'blob'
        }
      );

      downloadFileFromBlob(
        new Blob([response.data]),
        'relatorio_certificados_emitidos.csv'
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.status === 404) {
        notification.open?.({
          type: 'error',
          message: 'Download Error',
          description: 'Não há relatório de certificados disponível.'
        });
      } else {
        notification.open?.({
          type: 'error',
          message: 'Download Error',
          description: 'Erro ao baixar o relatório de certificados.'
        });
      }
    } finally {
      setDownloadingCsv(false);
    }
  }, [notification, appliedFilters]);

  // O limite viaja junto dos filtros porque o transporte embrulha tudo em
  // `filter[...]`. No backend ele nao e um `has_scope`: quem corta e o servico que
  // desenha, depois de ordenar. Vale so para os PDFs de impressao — o CSV e a
  // listagem inteira por definicao, e continua sem limite.
  const reportFilters = useMemo(
    () => ({
      ...(appliedFilters as Record<string, string>),
      ...(batchLimit ? { limit: String(batchLimit) } : {})
    }),
    [appliedFilters, batchLimit]
  );

  const handleGenerateMergedPdf = useCallback(
    () =>
      generate({
        endpoint: 'admin/graduate/courses/merged_certificates/report',
        kind: MERGED_PDF_KIND,
        filters: reportFilters,
        successMessage:
          'O PDF único está sendo gerado, e você será notificado quando estiver pronto para download.'
      }),
    [generate, reportFilters]
  );

  const handleGenerateAcademicRecords = useCallback(
    () =>
      generate({
        endpoint: 'admin/graduate/courses/merged_academic_records/report',
        kind: ACADEMIC_RECORDS_KIND,
        filters: reportFilters,
        successMessage:
          'O PDF dos históricos está sendo gerado, e você será notificado quando estiver pronto para download.'
      }),
    [generate, reportFilters]
  );

  const handleDownloadSelect = useCallback(
    (key: string) => {
      if (key === CSV_KIND) {
        handleDownloadCertificate();
      } else if (key === MERGED_PDF_KIND) {
        handleGenerateMergedPdf();
      } else {
        handleGenerateAcademicRecords();
      }
    },
    [
      handleDownloadCertificate,
      handleGenerateMergedPdf,
      handleGenerateAcademicRecords
    ]
  );

  // Opções do menu dropdown de importação

  const importMenuItems: MenuProps['items'] = [
    {
      label: 'Entrega da reguladora',
      key: 'regulator_delivery'
    },
    {
      label: 'Recebimento da secretaria',
      key: 'secretary_receipt'
    },
    {
      label: 'Código de rastreio',
      key: 'tracking_code'
    }
  ];

  // Função para lidar com o clique no menu
  const handleImportMenuClick: MenuProps['onClick'] = useCallback((e: any) => {
    setImportType(
      e.key as 'regulator_delivery' | 'secretary_receipt' | 'tracking_code'
    );
    setImportModalOpen(true);
  }, []);

  const importMenuProps = {
    items: importMenuItems,
    onClick: handleImportMenuClick
  };

  const pagination = tableProps.pagination;
  const total =
    pagination && typeof pagination === 'object' ? pagination.total : undefined;
  return (
    <List breadcrumb={null} title={''} canCreate={false}>
      <IssuedCertificatesFilter searchFormProps={searchFormProps} />
      <Space className="w-full justify-end mb-6">
        <Dropdown menu={importMenuProps}>
          <Button icon={<Icons.ImportOutlined />}>
            <Space>
              Importar
              <Icons.DownOutlined />
            </Space>
          </Button>
        </Dropdown>
        <Select
          aria-label="Quantidade de certificados por lote de impressão"
          className="w-48"
          value={batchLimit}
          onChange={setBatchLimit}
          options={BATCH_LIMIT_SELECT_OPTIONS}
        />
        <DownloadMenu
          hasFilter={Object.values(appliedFilters).some(Boolean)}
          busy={downloadingCsv}
          total={total}
          limit={batchLimit}
          reportId={reportId}
          runningKind={runningKind}
          onSelect={handleDownloadSelect}
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} itens`
        }}
      >
        <Table.Column
          fixed="left"
          dataIndex="registration_number"
          key="registration_number"
          title="Nº de registro"
          width={100}
          render={value => <TextField value={value} />}
        />
        <Table.Column<IIssuedCertificate>
          fixed="left"
          dataIndex="user"
          key="student"
          title="Aluno"
          width={300}
          render={(user, record) => (
            <StudentInfoCard
              user={user}
              registration={record.enrollment_registration}
            />
          )}
        />
        <Table.Column
          dataIndex="course"
          key="course"
          title="Curso"
          width={300}
          render={(_, record: IIssuedCertificate) => (
            <Card size="small" className="border-0 shadow-none bg-transparent">
              <Text strong className="block">
                {record.course_name}
              </Text>
              <Text type="secondary" className="block text-sm">
                Código: {record.course_code || '-'}
              </Text>
            </Card>
          )}
        />
        <Table.Column
          dataIndex="registered_at"
          key="registered_at"
          title="Data de registro"
          width={110}
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        <Table.Column
          dataIndex="issued_at"
          key="issued_at"
          title="Data de expedição"
          width={110}
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        <Table.Column<IIssuedCertificate>
          dataIndex="last_exported_at"
          key="last_exported_at"
          title="Exportado em"
          width={110}
          render={(_, record) => (
            <TextField
              value={
                record.last_exported_at
                  ? formatDate(record.last_exported_at, 'DD/MM/YYYY')
                  : '-'
              }
            />
          )}
        />
        <Table.Column<IIssuedCertificate>
          dataIndex="last_shipment"
          key="sent_by_regulator_at"
          title="Entregue pela reguladora"
          width={140}
          render={(_, record) => (
            <TextField
              value={
                record.last_shipment?.sent_by_regulator_at
                  ? formatDate(
                      record.last_shipment.sent_by_regulator_at,
                      'DD/MM/YYYY'
                    )
                  : '-'
              }
            />
          )}
        />
        <Table.Column<IIssuedCertificate>
          dataIndex="last_shipment"
          key="received_by_secretary_at"
          title="Recebido pela secretaria"
          width={145}
          render={(_, record) => (
            <TextField
              value={
                record.last_shipment?.received_by_secretary_at
                  ? formatDate(
                      record.last_shipment.received_by_secretary_at,
                      'DD/MM/YYYY'
                    )
                  : '-'
              }
            />
          )}
        />
        <Table.Column<IIssuedCertificate>
          dataIndex="last_shipment"
          key="posted_at"
          title="Enviado ao aluno"
          width={140}
          render={(_, record) => (
            <TextField
              value={
                record.last_shipment?.posted_at
                  ? formatDate(record.last_shipment.posted_at, 'DD/MM/YYYY')
                  : '-'
              }
            />
          )}
        />
        <Table.Column<IIssuedCertificate>
          dataIndex="last_shipment"
          key="tracking_code"
          title="Código de rastreio"
          width={140}
          render={(_, record) => (
            <TextField value={record.last_shipment?.tracking_code ?? '-'} />
          )}
        />
        <Table.Column<IIssuedCertificate>
          fixed="right"
          title="Ações"
          width={80}
          dataIndex="actions"
          render={(_, record) => {
            return (
              <ShowButton
                hideText
                size="small"
                recordItemId={record.enrollment_id}
              />
            );
          }}
        />
      </Table>

      <ImportCertificateModal
        modalProps={{
          open: importModalOpen,
          onCancel: () => setImportModalOpen(false)
        }}
        modalClose={() => setImportModalOpen(false)}
        importType={importType}
        refetch={() => {
          searchFormProps.form?.submit();
        }}
      />
    </List>
  );
};

export default IssuedCertificates;
